/**
 * THE HAND-WRITTEN OLYMPIAD BANK, described once.
 *
 * The server owns which paper ids exist (PAPER_ID_RE in kangaroo-math.js: grade 3 to 8, paper 1
 * to 5). These two rules mirror it so the marketing pages, the picker and the per-paper routes
 * all quote the same blueprint instead of three drifting copies. Numbers verified against the
 * paper files: grade 3-4 papers hold 35 questions for 40 marks, grades 5-8 hold 50 for 60.
 */

/**
 * How many questions a signed-out visitor may answer before the wall. Enough to prove the papers
 * are real and hand-written; not enough to hand over all 1,350.
 */
export const FREE_PREVIEW_QUESTIONS = 5;

export const OLYMPIAD_GRADES = [3, 4, 5, 6, 7, 8] as const;
export const OLYMPIAD_PAPERS = [1, 2, 3, 4, 5] as const;

export type OlympiadGrade = (typeof OLYMPIAD_GRADES)[number];

export const isJunior = (grade: number) => grade <= 4;

/** Every paper id the server will serve, e.g. "grade5-paper-2". */
export const allPaperIds = () =>
  OLYMPIAD_GRADES.flatMap(g => OLYMPIAD_PAPERS.map(p => `grade${g}-paper-${p}`));

/** "grade5-paper-2" → { grade: 5, paper: 2 }, or null when the id is not one of ours. */
export function parsePaperId(id: string): { grade: OlympiadGrade; paper: number } | null {
  const m = /^grade([3-8])-paper-([1-5])$/.exec(id);
  if (!m) return null;
  return { grade: Number(m[1]) as OlympiadGrade, paper: Number(m[2]) };
}

export function paperShape(grade: number) {
  return isJunior(grade)
    ? { questions: 35, marks: 40, minutes: 60, achievers: 5, achieverMarks: 2,
        sections: '10 Logical Reasoning, 10 Mathematical Reasoning, 10 Everyday Mathematics' }
    : { questions: 50, marks: 60, minutes: 60, achievers: 5, achieverMarks: 3,
        sections: '15 Logical Reasoning, 20 Mathematical Reasoning, 10 Everyday Mathematics' };
}

/**
 * Which Math Kangaroo level a grade sits in. Kangaroo groups two school years per level, and
 * these are the official names, so a parent searching "Ecolier" lands on the right page.
 */
export const KANGAROO_LEVELS: Record<number, { name: string; grades: string }> = {
  3: { name: 'Ecolier', grades: 'grades 3 and 4' },
  4: { name: 'Ecolier', grades: 'grades 3 and 4' },
  5: { name: 'Benjamin', grades: 'grades 5 and 6' },
  6: { name: 'Benjamin', grades: 'grades 5 and 6' },
  7: { name: 'Cadet', grades: 'grades 7 and 8' },
  8: { name: 'Cadet', grades: 'grades 7 and 8' },
};
